import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import Modal from '@/components/base/Modal';
import { useExpenses } from '@/hooks/useExpenses';
import { EXPENSE_CATEGORIES, deleteExpense, type Expense } from '@/services/expenses';
import { downloadCsv } from '@/lib/csv';
import ExpenseModal from './components/ExpenseModal';

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function ExpensesPage() {
  const { data, loading, error, refetch } = useExpenses();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [deleting, setDeleting] = useState<Expense | null>(null);
  const [deletingNow, setDeletingNow] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const categories = ['all', ...Array.from(new Set(data.map((e) => e.category)))];
  const total = data.reduce((s, e) => s + e.amount, 0);

  const filtered = data.filter((e) => {
    if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return e.description.toLowerCase().includes(q);
  });

  function handleExport() {
    downloadCsv(
      'gastos',
      ['Categoría', 'Descripción', 'Monto', 'Fecha'],
      filtered.map((e) => [
        EXPENSE_CATEGORIES[e.category] ?? e.category,
        e.description,
        e.amount,
        formatDate(e.expenseDate),
      ]),
    );
  }

  async function handleDelete() {
    if (!deleting) return;
    setDeletingNow(true);
    setDeleteError('');
    const { error } = await deleteExpense(deleting.id);
    setDeletingNow(false);
    if (error) {
      setDeleteError(error);
      return;
    }
    setDeleting(null);
    refetch();
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Gastos</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} gastos registrados</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              className="text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-download-line mr-1.5"></i>Exportar
            </button>
            <button
              onClick={() => setShowNew(true)}
              className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i>Nuevo Gasto
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Total gastos</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">${total.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Categorías usadas</p>
            <p className="text-xl font-bold text-accent-600 mt-1">{categories.length - 1}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Promedio por gasto</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">
              ${data.length ? (total / data.length).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategoryFilter(c)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  categoryFilter === c
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {c === 'all' ? 'Todas' : EXPENSE_CATEGORIES[c] ?? c}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs ml-auto">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por descripción..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Descripción</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Categoría</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Monto</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Fecha</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-24 text-center">Acción</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando gastos...
                    </td>
                  </tr>
                ) : error ? null : (
                  filtered.map((e) => (
                    <tr key={e.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                            <i className="ri-bill-line text-accent-600"></i>
                          </div>
                          <p className="text-foreground-900 font-medium">{e.description}</p>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap text-foreground-700 bg-background-100">
                          {EXPENSE_CATEGORIES[e.category] ?? e.category}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-foreground-950 whitespace-nowrap">
                        ${e.amount.toLocaleString()}
                      </td>
                      <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(e.expenseDate)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setEditing(e)}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            title="Editar"
                          >
                            <i className="ri-pencil-line text-foreground-500"></i>
                          </button>
                          <button
                            onClick={() => {
                              setDeleting(e);
                              setDeleteError('');
                            }}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                            title="Eliminar"
                          >
                            <i className="ri-delete-bin-line text-red-500"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-bill-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {data.length === 0
                  ? 'Aún no hay gastos registrados.'
                  : 'No se encontraron gastos con ese filtro.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {(showNew || editing) && (
        <ExpenseModal
          expense={editing}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
          }}
          onSaved={refetch}
        />
      )}

      {deleting && (
        <Modal
          title="Eliminar gasto"
          subtitle={`Estás a punto de eliminar "${deleting.description}".`}
          onClose={() => {
            setDeleting(null);
            setDeleteError('');
          }}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setDeleting(null);
                  setDeleteError('');
                }}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deletingNow}
                className="px-4 py-2 text-sm font-semibold bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deletingNow ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Eliminando...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line mr-1.5"></i>Eliminar
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            {deleteError && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{deleteError}</span>
              </div>
            )}
            <p className="text-sm text-foreground-600">
              Esta acción no se puede deshacer. El gasto se eliminará de forma permanente.
            </p>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}